import { type ReactElement, type ReactNode } from 'react';

import { cn } from '@/lib/utils';
import { InfoHint } from './info-hint';
import { Label } from './label';

/** Props injected into the child input by FormField. */
export interface FieldInputProps {
  readonly id: string;
  readonly 'aria-required'?: true;
  readonly 'aria-invalid': boolean;
  readonly 'aria-describedby'?: string;
  readonly className: string;
}

interface FormFieldProps {
  readonly id: string;
  readonly label: ReactNode;
  readonly required?: boolean;
  readonly error?: string | undefined;
  /** Optional help text shown via an info icon next to the label. */
  readonly hint?: string;
  /** Additional className on the outer wrapper div (e.g. for grid span). */
  readonly className?: string;
  /** Additional className forwarded to the Label element (e.g. "text-xs"). */
  readonly labelClassName?: string;
  /** Render prop — receives pre-built aria + className props for the input. */
  readonly children: (fieldProps: FieldInputProps) => ReactElement;
}

/**
 * Wraps a labelled form input with a consistent label, required indicator,
 * and inline error message. Pass the input as a render prop so that the
 * correct `id`, `aria-*`, and `className="mt-1"` props are automatically wired.
 *
 * Works with Input, DateInput, and SelectTrigger (all accept FieldInputProps).
 *
 * @example
 * <FormField id="s1-firstName" label={t('...')} required error={fieldErrors.firstName}>
 *   {(fp) => <Input {...fp} value={...} onChange={...} />}
 * </FormField>
 */
export function FormField({
  id,
  label,
  required,
  error,
  hint,
  className,
  labelClassName,
  children,
}: FormFieldProps): ReactElement {
  const errorId = `${id}-error`;

  const fieldProps: FieldInputProps = {
    id,
    ...(required ? { 'aria-required': true as const } : {}),
    'aria-invalid': error !== undefined,
    ...(error ? { 'aria-describedby': errorId } : {}),
    className: 'mt-1',
  };

  return (
    <div className={cn(className)}>
      <div className="flex items-center gap-1.5">
        <Label htmlFor={id} className={labelClassName}>
          {label}
          {required && <span aria-hidden="true"> *</span>}
        </Label>
        {hint !== undefined && hint.length > 0 && <InfoHint text={hint} />}
      </div>
      {children(fieldProps)}
      {error && (
        <p id={errorId} role="alert" className="mt-1 text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}
